import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import './pin-keypad';
import type { PinKeypad } from './pin-keypad';
import { ApiFactory } from '../api/easypos-api';
import type { IStaffPinIdentity } from '../api/easypos-api';
import { getOrCreateDeviceId } from '../datatypes/device';
import { styles as sharedStyles } from '../styles/shared-styles';

// Who is serving is exactly who a PIN resolved to - the same shape the server
// answers with, deliberately not a parallel one that could drift from it.
export type IServingStaff = IStaffPinIdentity;

// sessionStorage, not localStorage: who is serving is true for as long as this
// tab is open at this till, and a browser restart is a good enough proxy for
// "the day moved on". Keyed by shop so two shops in two tabs cannot cross.
//
// These live at module scope rather than on the element because the till has to
// be able to forget who is serving when the shift ends, and the bar is not on
// screen at that moment - the till has already switched to its no-open-day gate.
// One home for the key beats two places that could disagree about it.
const servingKey = (shopName: string) => `easypos.serving.${shopName}`;

export function readServingStaff(shopName: string): IServingStaff | null {
  if (!shopName) return null;
  try {
    const raw = sessionStorage.getItem(servingKey(shopName));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as IServingStaff;
    // Defensive: a hand-edited or half-written entry must not put a meaningless
    // id on a sale. Only a shaped staff id is restored.
    if (
      parsed &&
      typeof parsed.loginId === 'string' &&
      parsed.loginId.startsWith('staff-')
    ) {
      return parsed;
    }
    return null;
  } catch {
    // Storage can be unavailable (private mode, quota, disabled). Losing the
    // restored name costs one PIN entry; failing the till would cost a sale.
    return null;
  }
}

export function writeServingStaff(
  shopName: string,
  serving: IServingStaff | null
): void {
  if (!shopName) return;
  try {
    if (serving) {
      sessionStorage.setItem(servingKey(shopName), JSON.stringify(serving));
    } else {
      sessionStorage.removeItem(servingKey(shopName));
    }
  } catch {
    // Same reasoning as readServingStaff: never let storage fail the till. The
    // name still applies to this page's sales; it just will not survive a reload.
  }
}

// Forget who is serving. Called when the shift ends - the shift is the handover
// the architect named, so the name goes with it rather than sitting on screen
// for whoever opens the next one.
export function clearServingStaff(shopName: string): void {
  writeServingStaff(shopName, null);
}

// N.7.4 - the till's "who is serving" bar.
//
// This is ATTRIBUTION, not authentication, and the distinction drives every
// decision here. The device already holds the shop's session; a staff PIN issues
// nothing, changes no shift, and grants no permission. All it does is put a name
// on the sales taken next. The architect's @LOCK-OPEN-002 correction is the
// specification: "on a shift there might be a number of people logging in to
// take sales... just record the person taking the sale."
//
// Consequences that are easy to get wrong, so they are stated:
//
//   - Identifying yourself is OPTIONAL. Nothing here may block a sale. A till
//     where nobody has tapped in still sells, unattributed (@STAFF-SELL-002) -
//     a queue of customers must never wait on a record-keeping step.
//   - The name survives a page reload but not the shift. It is held in
//     sessionStorage so a refresh mid-rush does not lose it, and cleared when
//     the shift ends, because the shift is the handover the architect named.
//     It deliberately does NOT idle-expire: the model is trust-based by design,
//     and a keypad that re-challenged a cashier through a quiet morning would
//     buy no safety while costing real time at the counter.
//   - The bar is hidden entirely below Silver. staff/verify-pin is tier-gated,
//     so offering the keypad on a Free shop would be a button whose every press
//     the server refuses.
@customElement('till-staff-bar')
export class TillStaffBar extends LitElement {
  @property({ type: String }) shopName = '';

  // Mirrors the shop's AllowsMultiStaffPins. False hides the bar completely.
  @property({ type: Boolean }) enabled = false;

  @state() private _serving: IServingStaff | null = null;
  @state() private _dialogOpen = false;
  @state() private _busy = false;

  @query('pin-keypad') private _keypad!: PinKeypad | null;

  private _deviceId: string | null = null;

  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
      }
      .bar {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        width: 100%;
        box-sizing: border-box;
        padding: 0.45rem 0.6rem;
        border: 1px dashed var(--app-border);
        border-radius: var(--sl-border-radius-medium);
        background: transparent;
        color: var(--app-text-muted);
        font: inherit;
        font-size: 0.85rem;
        cursor: pointer;
        text-align: left;
      }
      .bar:hover {
        background: var(--sl-color-neutral-50);
      }
      .bar.serving {
        border-style: solid;
        border-color: var(--sl-color-primary-300);
        background: var(--sl-color-primary-50);
        color: var(--app-text);
        cursor: default;
      }
      .bar.serving:hover {
        background: var(--sl-color-primary-50);
      }
      .who {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        line-height: 1.25;
      }
      .who strong {
        font-weight: 600;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .who small {
        color: var(--app-text-muted);
        font-size: 0.75rem;
      }
      .change {
        appearance: none;
        border: none;
        background: none;
        color: var(--sl-color-primary-700);
        font: inherit;
        font-size: 0.78rem;
        text-decoration: underline;
        cursor: pointer;
        padding: 0 0.25rem;
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();
    void this._loadDeviceId();
  }

  updated(changed: Map<string, unknown>) {
    // Restore on first sight of a shop, and re-restore when the till is pointed
    // at a different one - a name from shop A must never carry into shop B.
    if (changed.has('shopName')) this._restore();
  }

  private async _loadDeviceId() {
    this._deviceId = await getOrCreateDeviceId();
  }

  private _restore(): void {
    this._serving = readServingStaff(this.shopName);
    this._emit();
  }

  private _persist(): void {
    writeServingStaff(this.shopName, this._serving);
  }

  private _emit(): void {
    this.dispatchEvent(
      new CustomEvent<{ serving: IServingStaff | null }>('serving-changed', {
        detail: { serving: this._serving },
        bubbles: true,
        composed: true,
      })
    );
  }

  // The operator dismissing the name themselves. The shift-end case goes through
  // clearServingStaff() instead, because the bar is unmounted by then.
  clearServing(): void {
    if (!this._serving) return;
    this._serving = null;
    this._persist();
    this._emit();
  }

  private async _openDialog() {
    this._dialogOpen = true;
    await this.updateComplete;
    this._keypad?.clear();
    this._keypad?.focusPad();
  }

  private async _onPinSubmit(e: CustomEvent<{ pin: string }>) {
    const pad = this._keypad;
    if (this._busy || !pad) return;
    if (!this._deviceId) {
      pad.reject('This device is not set up yet. Try again in a moment.');
      return;
    }
    this._busy = true;
    const r = await ApiFactory.authenticated().verifyStaffPin(
      this.shopName,
      this._deviceId,
      e.detail.pin
    );
    this._busy = false;

    if (!r.success || !r.data) {
      // The server sends a countdown with a 429 lockout; the pad runs it.
      pad.reject(
        r.error ?? 'Incorrect PIN.',
        r.status === 429 ? r.lockedSeconds : undefined
      );
      return;
    }

    this._serving = {
      loginId: r.data.loginId,
      displayName: r.data.displayName,
      jobTitle: r.data.jobTitle ?? null,
    };
    this._persist();
    this._emit();
    this._dialogOpen = false;
  }

  render() {
    if (!this.enabled) return nothing;

    return html`
      ${
        this._serving
          ? html`
              <div class="bar serving">
                <sl-icon name="person-badge"></sl-icon>
                <span class="who">
                  <strong>${this._serving.displayName}</strong>
                  <small
                    >Serving${
                      this._serving.jobTitle
                        ? ` · ${this._serving.jobTitle}`
                        : ''
                    }</small
                  >
                </span>
                <button class="change" @click=${this._openDialog}>
                  Change
                </button>
                <sl-icon-button
                  name="x-lg"
                  label="Stop recording who is serving"
                  @click=${this.clearServing}
                ></sl-icon-button>
              </div>
            `
          : html`
              <button class="bar" @click=${this._openDialog}>
                <sl-icon name="person"></sl-icon>
                <span>Who's serving?</span>
              </button>
            `
      }

      <sl-dialog
        class="full-page-on-phone"
        label="Who's serving?"
        ?open=${this._dialogOpen}
        @sl-request-close=${() => (this._dialogOpen = false)}
        @sl-after-hide=${() => (this._dialogOpen = false)}
      >
        <pin-keypad
          submit-label="That's me"
          hint="Enter your PIN so the sales you take carry your name. This does not sign you in, and you can sell without it."
          .busy=${this._busy}
          @pin-submit=${this._onPinSubmit}
        ></pin-keypad>
      </sl-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'till-staff-bar': TillStaffBar;
  }
}
